import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { Icon } from '@/kit/Icon';
import { useSidebarCollapsed } from '@/core/sidebar';

export interface AsideItem {
  label: string;
  icon: string;
  /** Route to link to. Omit for an action item (Angular `<a (click)>` with no routerLink). */
  to?: string;
  onClick?: () => void;
  /**
   * Further routes that also mark the item active: Angular's `routerLinkActive` sits on the item's wrapper, so a hidden
   * `<button routerLink="/home/manage/addClient">` inside it (the Add Client / Add User buttons) lights the item up.
   */
  alsoActiveFor?: string[];
  /** The `title` of the icon (Angular used it as the tooltip; it differs from the label in one place). */
  iconTitle?: string;
  /**
   * 3.0 sub-menu entries (General Settings > Limited Company > UK-GAAP / FRS102 / FRS105), listed under the item so they
   * can be reached from every page of the shell. They are left out while the item is active: its page then carries the
   * same choices in a menu of its own.
   */
  subItems?: { label: string; to: string }[];
}

/** Angular routerLinkActive (non-exact): active when the link is a segment-wise prefix of the current path. */
export function isLinkActive(pathname: string, to: string): boolean {
  const path = pathname.split(';')[0].replace(/\/+$/, '');
  const target = to.replace(/\/+$/, '');
  return path === target || path.startsWith(`${target}/`);
}

/** The left menu shared by the Manage, Settings and Taxreturn shells (`.wrapper > .aside + .container`). */
export function AsideLayout({
  items,
  children,
  collapsible = true,
}: {
  items: AsideItem[];
  children: ReactNode;
  /** false: no floating minimize arrow and the menu is always shown (Manage, whose client list has its own arrow). */
  collapsible?: boolean;
}) {
  const { pathname } = useLocation();
  const [storedCollapsed, setCollapsed] = useSidebarCollapsed();
  const collapsed = collapsible && storedCollapsed;

  return (
    <div className={`wrapper${collapsed ? ' aside-collapsed' : ''}`}>
      <div className={`aside${collapsed ? ' d-none' : ''}`}>
        <div className="aside-menu">
          <div className="aside-menu-wrapper">
            {items.map((item) => {
              const content = (
                <div className="d-flex align-items-center wp-100">
                  <Icon name={item.icon} outlined title={item.iconTitle ?? item.label} />
                  <span>{item.label}</span>
                </div>
              );
              const active = [item.to, ...(item.alsoActiveFor ?? [])].some((to) => !!to && isLinkActive(pathname, to));
              return (
                <div className={`menu-item${active ? ' active' : ''}`} key={item.label}>
                  <div className="d-flex justify-space-between">
                    {item.to ? (
                      <Link className="menu-link" to={item.to}>
                        {content}
                      </Link>
                    ) : (
                      <a className="menu-link" onClick={item.onClick}>
                        {content}
                      </a>
                    )}
                  </div>
                  {item.subItems && !active && (
                    <ul className="menu-sub-items">
                      {item.subItems.map((sub) => (
                        <li key={sub.label}>
                          <Link className="menu-sub-link" to={sub.to}>
                            {sub.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {collapsible && (
        <div
          className={`menu-toggle aside-toggle collapse-arrow ${collapsed ? 'toggle-icon' : ''}`.trim()}
          role="button"
          tabIndex={0}
          aria-label={collapsed ? 'maximise' : 'minimize'}
          title={collapsed ? 'maximise' : 'minimize'}
          data-testid={collapsed ? 'maximise' : 'minimize'}
          style={{ left: collapsed ? 10 : 115 }}
          onClick={() => setCollapsed(!collapsed)}
        >
          <Icon name={collapsed ? 'keyboard_arrow_right' : 'keyboard_arrow_left'} />
          <span className="sr-only" style={{ display: 'none' }}>
            {collapsed ? 'maximise' : 'minimize'}
          </span>
        </div>
      )}
      <div className="container" style={collapsed ? { width: '100%', marginLeft: 0 } : undefined}>
        {children}
      </div>
    </div>
  );
}
